import { Injectable, computed, signal } from '@angular/core';
import {
  HumanVisionDetectionTarget,
  HumanVisionResult,
  HumanVisionWorkerRequest,
  HumanVisionWorkerResponse,
  humanVisionResultSatisfiesTarget,
} from './human-vision.protocol';

export type HumanVisionPreviewState = 'idle' | 'loading' | 'person' | 'no-person' | 'error';

export interface HumanVisionBatchFrame {
  frame: ImageBitmap;
  timestampMs: number;
}

@Injectable({ providedIn: 'root' })
export class HumanVisionService {
  private worker: Worker | null = null;
  private inFlight = false;
  private requestId = 0;
  private nextDetectionAt = 0;
  private latest: HumanVisionResult | null = null;
  private currentTarget: HumanVisionDetectionTarget | null = null;
  private readonly stateSignal = signal<HumanVisionPreviewState>('idle');

  readonly state = this.stateSignal.asReadonly();
  readonly noPersonDetected = computed(() => {
    const state = this.stateSignal();
    return state === 'no-person' || state === 'error';
  });

  requestDetection(
    source: CanvasImageSource,
    timestampMs: number,
    target: HumanVisionDetectionTarget | null,
  ): void {
    this.currentTarget = target;
    if (!target) {
      this.stateSignal.set('idle');
      return;
    }
    const now = typeof performance !== 'undefined' ? performance.now() : Date.now();
    if (this.inFlight || now < this.nextDetectionAt || typeof createImageBitmap === 'undefined') return;
    this.inFlight = true;
    this.nextDetectionAt = now + 90;
    const requestId = ++this.requestId;
    if (!this.latest) this.stateSignal.set('loading');
    void createImageBitmap(source)
      .then(frame => {
        const worker = this.ensureWorker();
        const request: HumanVisionWorkerRequest = { type: 'detect', requestId, timestampMs, target, frame };
        worker.postMessage(request, [frame]);
      })
      .catch(() => {
        this.inFlight = false;
        this.stateSignal.set('error');
      });
  }

  latestFor(target: HumanVisionDetectionTarget | null): HumanVisionResult | null {
    return humanVisionResultSatisfiesTarget(this.latest, target) ? this.latest : null;
  }

  /**
   * Sequential batch analysis on a dedicated instance of the existing vision worker.
   * Auto-Reframe uses this so it cannot interrupt the live preview detector.
   */
  analyzeBatch(
    frames: HumanVisionBatchFrame[],
    target: HumanVisionDetectionTarget,
    signal?: AbortSignal,
    onProgress?: (completed: number, total: number) => void,
  ): Promise<HumanVisionResult[]> {
    if (!frames.length) return Promise.resolve([]);
    return new Promise((resolve, reject) => {
      const worker = new Worker(new URL('./human-vision.worker', import.meta.url), { type: 'module' });
      const results: HumanVisionResult[] = [];
      let index = 0;
      let settled = false;

      const closeRemaining = (): void => {
        for (let cursor = index; cursor < frames.length; cursor++) {
          try { frames[cursor]?.frame.close(); } catch { /* already transferred */ }
        }
      };
      const finish = (error?: Error): void => {
        if (settled) return;
        settled = true;
        signal?.removeEventListener('abort', abort);
        worker.terminate();
        closeRemaining();
        if (error) reject(error);
        else resolve(results);
      };
      const abort = (): void => finish(new DOMException('Auto-Reframe cancelled.', 'AbortError'));
      const postNext = (): void => {
        if (signal?.aborted) { abort(); return; }
        if (index >= frames.length) { finish(); return; }
        const current = frames[index]!;
        const requestId = index + 1;
        index++;
        const request: HumanVisionWorkerRequest = {
          type: 'detect', requestId, timestampMs: current.timestampMs, target, frame: current.frame,
        };
        worker.postMessage(request, [current.frame]);
      };

      worker.onmessage = ({ data }: MessageEvent<HumanVisionWorkerResponse>) => {
        if (data.type === 'error') {
          finish(new Error(data.message || 'Human vision analysis failed.'));
          return;
        }
        results.push(data.result);
        onProgress?.(results.length, frames.length);
        postNext();
      };
      worker.onerror = () => finish(new Error('Human vision worker failed.'));
      signal?.addEventListener('abort', abort, { once: true });
      postNext();
    });
  }

  reset(): void {
    this.requestId++;
    this.inFlight = false;
    this.latest = null;
    this.currentTarget = null;
    this.stateSignal.set('idle');
    this.worker?.terminate();
    this.worker = null;
  }

  private ensureWorker(): Worker {
    if (this.worker) return this.worker;
    const worker = new Worker(new URL('./human-vision.worker', import.meta.url), { type: 'module' });
    worker.onmessage = ({ data }: MessageEvent<HumanVisionWorkerResponse>) => {
      if (data.type === 'error') {
        if (data.requestId === this.requestId) {
          this.inFlight = false;
          this.stateSignal.set('error');
        }
        return;
      }
      if (data.result.requestId !== this.requestId) return;
      this.inFlight = false;
      this.latest = data.result;
      const target = this.currentTarget;
      this.stateSignal.set(humanVisionResultSatisfiesTarget(data.result, target) ? 'person' : 'no-person');
    };
    worker.onerror = () => {
      this.inFlight = false;
      this.stateSignal.set('error');
    };
    this.worker = worker;
    return worker;
  }
}
